import { DEFERA_CAPITAL_HOLD_FEE_RATE, SLICE_PAY_HOLD_FEE_RATE, getDefaultInstallmentDepositPercentForLeadDays } from './paymentScheduleCalculator';

import { useState, useEffect } from 'react';

export function createPageUrl(pageName: string) {
    return '/' + pageName.toLowerCase().replace(/ /g, '-');
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

/**
 * Parse monetary value from string or number
 * Handles formatted currency strings like "$1,299.00" or "1,299.00"
 * Returns 0 for invalid/NaN values with console warning
 */
export function parseMonetaryValue(value: any): number {
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }
  
  if (typeof value === 'string') {
    // Remove currency symbols ($, €, £, etc.) and commas
    const cleaned = value.replace(/[$€£¥,]/g, '').trim();
    const parsed = parseFloat(cleaned);
    
    if (isNaN(parsed)) {
      console.warn(`Failed to parse monetary value: "${value}". Returning 0.`);
      return 0;
    }
    
    return parsed;
  }
  
  // Fallback for null, undefined, or other types
  return 0;
}

/**
 * Tiered service fee on base (cash) fare (effective 2026-05-19).
 * - **Defera Capital** (`deferaCapitalTier`): under $300 → 30%; $300+ → 8%
 * - **Slice Pay / default**: under $300 → 40%; $300+ → 8%
 *
 * IMPORTANT: This schedule applies to NEW bookings only. Existing bookings
 * keep whatever rate was charged at booking time (their `service_fee` was
 * persisted to the bookings row and to Stripe at creation, so they continue
 * to bill against the original schedule). Do not retroactively recompute SF
 * on stored bookings — that would diverge from the customer's locked-in
 * Stripe subscription.
 *
 * The previous schedule (pre-2026-05-19) was:
 * - Defera Capital: under $200 → 30%; $200–$699 → 10%; $700+ → 8%
 * - Slice Pay:      under $200 → 40%; $200–$699 → 10%; $700+ → 8%
 * Flat-rate products (Defera Zero, Reserve, change-flight top-ups) are not
 * affected — they continue to use a flat 11% computed in their own paths.
 */
export function getServiceFeeRate(basePrice: number, deferaCapitalTier = false): number {
  const under300 = deferaCapitalTier ? 0.3 : 0.4;
  if (basePrice < 300) {
    return under300;
  }
  return 0.08;
}

/**
 * Estimated "due today" deposit from a raw fare, using the real checkout
 * math: the lead-time deposit tier applied to the pre-hold subtotal
 * (fare + tiered service fee) — the same basis the payment schedule uses.
 * Unknown departure dates fall back to the 20% mid-ladder tier rather than
 * the far-out 5%, so a teaser with no date never understates a close-in
 * booking too wildly. Used by estimate surfaces (Explore).
 */
export function estimatedDepositFromFare(
  baseFare: number,
  leadDays: number | null,
  deferaCapitalTier = false
): number {
  const subtotal = baseFare + calculateServiceFee(baseFare, deferaCapitalTier);
  const pct = Number.isFinite(leadDays as number)
    ? getDefaultInstallmentDepositPercentForLeadDays(leadDays as number, { deferaCapital: deferaCapitalTier })
    : 20;
  return subtotal * (pct / 100);
}

/**
 * All-in trip total from a raw fare on the standard financed path — the same
 * math checkout bills: fare + tiered service fee, plus the 15% hold on that
 * subtotal. Used by shopping surfaces (Explore estimates) so a number labeled
 * "total" always matches what Review would show for that fare.
 */
export function allInTotalFromFare(baseFare: number, deferaCapitalTier = false): number {
  const subtotal = baseFare + calculateServiceFee(baseFare, deferaCapitalTier);
  return subtotal * 1.15;
}

/**
 * Calculate service fee amount based on base price
 */
export function calculateServiceFee(basePrice: number, deferaCapitalTier = false): number {
  return basePrice * getServiceFeeRate(basePrice, deferaCapitalTier);
}

/**
 * Get service fee rate as percentage string for display
 */
export function getServiceFeeLabel(basePrice: number, deferaCapitalTier = false): string {
  const rate = getServiceFeeRate(basePrice, deferaCapitalTier);
  if (rate === 0.3) return '30%';
  if (rate === 0.4) return '40%';
  return '8%';
}

/**
 * Get service fee label for display - returns '0%' when service fee is waived (e.g. promo, flight+hotel)
 */
export function getServiceFeeDisplayLabel(
  serviceFee: number,
  basePrice: number,
  deferaCapitalTier = false
): string {
  if (serviceFee === 0 || !Number.isFinite(serviceFee)) return '0%';
  return getServiceFeeLabel(basePrice, deferaCapitalTier);
}

/**
 * Calculate Defera Miles earned from booking
 * Formula: 2 miles per $1 spent (2.5% back at a Mile's 1.25¢ trip value)
 * Based on grand_total_with_hold (final amount paid)
 * Uses floor for rewards (user doesn't get fractional rewards)
 */
export function calculateMilesEarned(totalAmountPaid: number): number {
  return Math.floor(totalAmountPaid * 2);
}

/** Ledger rows that earn Miles: manual/API awards, completed bookings, admin injects. */
const MILES_EARN_TYPES = ['earned', 'booking_earn', 'admin_grant'];

/**
 * Lifetime Miles earned and redeemed from one customer's ledger rows (the
 * admin customer page). Redeemed is net of Miles given back: a checkout's hold
 * ('redemption') that came back as a 'redemption_refund' (a failed payment or
 * a cancelled trip, migration 162) was never spent, so leaving the returns
 * out overstated it.
 */
export function lifetimeMilesTotals(
  transactions: Array<{ transaction_type?: string | null; amount?: number | string | null }> | null | undefined
): { earned: number; redeemed: number } {
  let earned = 0;
  let redeemed = 0;
  for (const t of transactions || []) {
    const amount = parseFloat(String(t.amount ?? 0)) || 0;
    const type = String(t.transaction_type || '');
    if (MILES_EARN_TYPES.includes(type)) {
      if (amount > 0) earned += amount;
    } else if (type.includes('redemption')) {
      redeemed -= amount;
    }
  }
  return { earned, redeemed: Math.max(0, redeemed) };
}

/**
 * The "Miles" and "Miles + Installments" payment methods, retired 2026-10-05:
 * they parked the cart for ops to finish by hand, and no customer ever
 * completed one. Miles are applied at checkout now (Review's Miles panel),
 * at 1.25¢ each (supabase/functions/_shared/miles-redemption.js).
 */
const RETIRED_MILES_PAYMENT_METHODS = new Set(['miles', 'miles_installments']);

/**
 * A payment method as checkout still offers it: a retired Miles method (an
 * old link, a resumed cart, a stale selection) reads as installments, the way
 * Review reads it, so no page prices a trip at the old 1¢ a Mile.
 */
export function withoutRetiredMilesMethod(paymentMethod: string | null | undefined): string | null | undefined {
  return paymentMethod && RETIRED_MILES_PAYMENT_METHODS.has(paymentMethod) ? 'installments' : paymentMethod;
}

/**
 * Convert dollar amount to miles for redemption
 * Formula: 100 miles per $1 (1 mile = $0.01 redemption value)
 * CRITICAL: Uses Math.ceil to ensure full coverage - prevents underpayment
 * Example: $499.99 needs 50,000 miles (not 49,999) to fully cover the amount
 *
 * The retired Miles payment methods' 1¢ rate: it reads carts saved with
 * them. A Mile on a trip is worth 1.25¢ (tripCentsForMiles), and no live page
 * reaches this with a trip price (withoutRetiredMilesMethod).
 */
export function dollarsToMiles(dollars: number): number {
  return Math.ceil(dollars * 100);
}

/**
 * Convert miles to dollar amount for redemption
 * Formula: 1 mile = $0.01 (100 miles = $1)
 *
 * The retired 1¢ checkout rate, like dollarsToMiles: for carts saved with the
 * old Miles methods only. A Mile on a trip is worth 1.25¢ (tripCentsForMiles).
 */
export function milesToDollars(miles: number): number {
  return miles * 0.01;
}

/**
 * Format miles in compact notation (e.g., 50000 → 50K, 1500 → 1.5K)
 * @param miles - Number of miles
 * @returns Compact formatted string (e.g., "50K", "1.5K", "500")
 */
export function formatMilesCompact(miles: number): string {
  if (miles >= 1000) {
    const thousands = miles / 1000;
    // Preserve one decimal place for values under 100K if needed
    if (thousands < 100 && thousands % 1 !== 0) {
      return `${thousands.toFixed(1)}K`;
    }
    // Round to nearest whole number for large values
    return `${Math.round(thousands)}K`;
  }
  return miles.toString();
}

/**
 * Calculate Review page pricing with credits-first, then miles, then fees logic
 * CRITICAL: Travel credits are applied to BASE FARE first, then fees are calculated on the reduced amount
 *
 * SEAT FEES: on financed paths (installments / defera_capital / defera_zero /
 * miles_installments) seats are FINANCED — they join `subtotalBeforeHold` so
 * they're spread across the installment schedule, and the hold fee applies to
 * them (Slice Pay mechanically adds its 15% to whatever amount we send; the
 * Stripe paths mirror that for parity). The service fee is calculated on
 * (base fare + seats) wherever a service fee applies — seats are part of the
 * fare we're fronting. Miles-only stays fee-free with seats added flat.
 *
 * @param baseFare - Base flight fare (before any deductions or fees)
 * @param seatTotal - Total seat selection costs (service + hold fees apply on fee-charging paths)
 * @param paymentMethod - 'miles', 'installments', or 'miles_installments'
 * @param milesBalance - User's available miles
 * @param creditsApplied - Total travel credits/gift cards applied (default 0)
 * @param waiveServiceFee - When true (e.g. flight+hotel packages), service fee is 0; base fares only
 * @param installmentsCheckoutProvider - For `miles_installments` only: selects which hold fee rate constant to use (Slice Pay or Defera Capital). Both rates are currently 15%.
 * @returns Complete pricing breakdown with explicit seat-excluded and seat-included totals
 */
export function calculateReviewPagePricing(
  baseFare: number,
  seatTotal: number,
  paymentMethod: string,
  milesBalance: number,
  creditsApplied: number = 0,
  waiveServiceFee: boolean = false,
  installmentsCheckoutProvider: 'slice_pay' | 'defera_capital' = 'slice_pay'
): {
  cashPortion: number;
  serviceFee: number;
  holdFee: number;
  seatTotal: number;
  /** Financed amount before hold fee = cashPortion + serviceFee + seatTotal (what we send to Slice Pay) */
  subtotalBeforeHold: number;
  /** What the trip would cost with no seats selected (subtotal-ex-seats + hold on it) */
  totalExcludingSeats: number;
  /** Full trip total = subtotalBeforeHold + holdFee (seats are inside the subtotal on financed paths) */
  grandTotalIncludingSeats: number;
  milesUsed: number;
  milesValue: number;
  creditsApplied: number;
  baseFareAfterCredits: number;
} {
  // CRITICAL: Apply travel credits to base fare FIRST, before any fee calculations
  // Credits reduce the base fare, which then reduces the fees proportionally
  const baseFareAfterCredits = Math.max(0, baseFare - creditsApplied);
  
  // For miles-only: NO fees, just (base - credits) + seats
  if (paymentMethod === 'miles') {
    const totalExcludingSeats = baseFareAfterCredits;
    const grandTotalIncludingSeats = baseFareAfterCredits + seatTotal;
    const milesNeeded = dollarsToMiles(grandTotalIncludingSeats);
    const milesUsed = Math.min(milesBalance, milesNeeded);
    
    return {
      cashPortion: 0,
      serviceFee: 0,
      holdFee: 0,
      seatTotal,
      subtotalBeforeHold: 0, // Miles-only: no cash to Slice Pay
      totalExcludingSeats,
      grandTotalIncludingSeats,
      milesUsed,
      milesValue: grandTotalIncludingSeats,
      creditsApplied,
      baseFareAfterCredits
    };
  }
  
  // For miles_installments: Deduct credits first, then miles from remaining, then calculate fees
  if (paymentMethod === 'miles_installments') {
    // Step 1: Credits already deducted from base fare (baseFareAfterCredits)
    // Step 2: Deduct miles from the credit-adjusted base fare
    const milesValueInDollars = milesToDollars(milesBalance);
    const cashPortion = Math.max(0, baseFareAfterCredits - milesValueInDollars);
    const milesUsed = Math.min(milesBalance, dollarsToMiles(baseFareAfterCredits));
    const actualMilesValue = milesToDollars(milesUsed);
    
    // Step 3: Calculate service fee on (CASH PORTION + seats) — seats are
    // always paid in cash, never miles — then finance seats: they join the
    // subtotal sent to the installment provider, so the hold fee applies too.
    // Flight+hotel: waive service fee; base fares only
    const capitalMi = installmentsCheckoutProvider === 'defera_capital';
    const serviceFee = waiveServiceFee ? 0 : calculateServiceFee(cashPortion + seatTotal, capitalMi);
    const holdRateMi =
      installmentsCheckoutProvider === 'defera_capital' ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;
    const subtotalBeforeHold = cashPortion + serviceFee + seatTotal;
    const holdFee = subtotalBeforeHold * holdRateMi;
    const serviceFeeExSeats = waiveServiceFee ? 0 : calculateServiceFee(cashPortion, capitalMi);
    const totalExcludingSeats = (cashPortion + serviceFeeExSeats) * (1 + holdRateMi);
    const grandTotalIncludingSeats = subtotalBeforeHold + holdFee;
    
    return {
      cashPortion,
      serviceFee,
      holdFee,
      seatTotal,
      subtotalBeforeHold,
      totalExcludingSeats,
      grandTotalIncludingSeats,
      milesUsed,
      milesValue: actualMilesValue,
      creditsApplied,
      baseFareAfterCredits
    };
  }
  
  // Defera Zero: $0 upfront, (base + seats) + 11% service fee + 15% hold fee (same as Installments, Stripe subscription)
  if (paymentMethod === 'defera_zero') {
    const zeroServiceFee = waiveServiceFee ? 0 : (baseFareAfterCredits + seatTotal) * 0.11;
    const subtotalBeforeHold = baseFareAfterCredits + zeroServiceFee + seatTotal;
    const holdFee = subtotalBeforeHold * 0.15;
    const totalExcludingSeats = baseFareAfterCredits * (waiveServiceFee ? 1 : 1.11) * 1.15;
    const grandTotalIncludingSeats = subtotalBeforeHold + holdFee;
    return {
      cashPortion: baseFareAfterCredits,
      serviceFee: zeroServiceFee,
      holdFee,
      seatTotal,
      subtotalBeforeHold,
      totalExcludingSeats,
      grandTotalIncludingSeats,
      milesUsed: 0,
      milesValue: 0,
      creditsApplied,
      baseFareAfterCredits
    };
  }

  // Reserve: (base fare + seats) + 11% service fee, no hold fee
  if (paymentMethod === 'reserve') {
    const reserveServiceFee = waiveServiceFee ? 0 : (baseFareAfterCredits + seatTotal) * 0.11;
    const reserveTotal = baseFareAfterCredits + reserveServiceFee + seatTotal;
    return {
      cashPortion: baseFareAfterCredits,
      serviceFee: reserveServiceFee,
      holdFee: 0,
      seatTotal,
      subtotalBeforeHold: baseFareAfterCredits + reserveServiceFee + seatTotal,
      totalExcludingSeats: baseFareAfterCredits * (waiveServiceFee ? 1 : 1.11),
      grandTotalIncludingSeats: reserveTotal,
      milesUsed: 0,
      milesValue: 0,
      creditsApplied,
      baseFareAfterCredits
    };
  }

  // Defera Capital: tiered service fee (30% / 8% — see getServiceFeeRate) on (base + seats); hold fee 15%
  if (paymentMethod === 'defera_capital') {
    const serviceFee = waiveServiceFee ? 0 : calculateServiceFee(baseFareAfterCredits + seatTotal, true);
    const subtotalBeforeHold = baseFareAfterCredits + serviceFee + seatTotal;
    const holdFee = subtotalBeforeHold * DEFERA_CAPITAL_HOLD_FEE_RATE;
    const serviceFeeExSeats = waiveServiceFee ? 0 : calculateServiceFee(baseFareAfterCredits, true);
    const totalExcludingSeats = (baseFareAfterCredits + serviceFeeExSeats) * (1 + DEFERA_CAPITAL_HOLD_FEE_RATE);
    const grandTotalIncludingSeats = subtotalBeforeHold + holdFee;
    return {
      cashPortion: baseFareAfterCredits,
      serviceFee,
      holdFee,
      seatTotal,
      subtotalBeforeHold,
      totalExcludingSeats,
      grandTotalIncludingSeats,
      milesUsed: 0,
      milesValue: 0,
      creditsApplied,
      baseFareAfterCredits
    };
  }

  // For regular installments (Slice Pay): tiered service fee (40% / 8% — see getServiceFeeRate)
  // calculated on (base + seats); flight+hotel waives service fee
  const serviceFee = waiveServiceFee ? 0 : calculateServiceFee(baseFareAfterCredits + seatTotal, false);
  // Seats are financed: included in the subtotal sent to Slice Pay, so the
  // 15% hold fee applies to them (Slice adds it to whatever amount we send)
  const subtotalBeforeHold = baseFareAfterCredits + serviceFee + seatTotal;
  const holdFee = subtotalBeforeHold * 0.15;
  const serviceFeeExSeats = waiveServiceFee ? 0 : calculateServiceFee(baseFareAfterCredits, false);
  const totalExcludingSeats = (baseFareAfterCredits + serviceFeeExSeats) * 1.15;
  const grandTotalIncludingSeats = subtotalBeforeHold + holdFee;
  
  return {
    cashPortion: baseFareAfterCredits,
    serviceFee,
    holdFee,
    seatTotal,
    subtotalBeforeHold,
    totalExcludingSeats,
    grandTotalIncludingSeats,
    milesUsed: 0,
    milesValue: 0,
    creditsApplied,
    baseFareAfterCredits
  };
}

/**
 * Calculate price to display based on payment method
 * @param basePrice - The original price in dollars
 * @param paymentMethod - 'installments', 'miles', or 'miles_installments'
 * @param milesBalance - User's available miles balance (optional, only needed for miles_installments)
 * @returns Object with display price and breakdown
 */
export function calculateDisplayPrice(
  basePrice: number,
  paymentMethod: string,
  milesBalance: number = 0
): {
  displayPrice: number;
  displayUnit: string;
  milesUsed?: number;
  remainingCash?: number;
  isFullyMilesCovered?: boolean;
} {
  switch (paymentMethod) {
    case 'installments':
      return {
        displayPrice: basePrice,
        displayUnit: '$'
      };

    case 'reserve':
      return {
        displayPrice: basePrice,
        displayUnit: '$'
      };

    case 'defera_zero': {
      // $0 upfront; show weekly price (base + 11% service fee, divided by ~12 weeks)
      const serviceFee = basePrice * 0.11;
      const total = basePrice + serviceFee;
      const weeklyPrice = total / 12;
      return {
        displayPrice: weeklyPrice,
        displayUnit: '/week'
      };
    }
    
    case 'miles':
      return {
        displayPrice: dollarsToMiles(basePrice),
        displayUnit: ' miles'
      };
    
    case 'miles_installments': {
      const pricing = calculateReviewPagePricing(basePrice, 0, 'miles_installments', milesBalance);
      
      if (pricing.milesUsed >= dollarsToMiles(basePrice)) {
        // Miles fully cover the base price
        return {
          displayPrice: pricing.milesUsed,
          displayUnit: ' miles',
          milesUsed: pricing.milesUsed,
          remainingCash: 0,
          isFullyMilesCovered: true
        };
      }
      
      // Miles partially cover - show cash PORTION (base) for consistency with installments display
      // Installments shows base fare ($259); miles_installments should show same base cash amount
      const cashPortion = Math.round(pricing.cashPortion * 100) / 100;
      return {
        displayPrice: cashPortion,
        displayUnit: '$',
        milesUsed: pricing.milesUsed,
        remainingCash: cashPortion,
        isFullyMilesCovered: false
      };
    }
    
    default:
      return {
        displayPrice: basePrice,
        displayUnit: '$'
      };
  }
}

/**
 * Format price for display based on payment method
 * @param basePrice - The original price in dollars
 * @param paymentMethod - 'installments', 'miles', or 'miles_installments'
 * @param milesBalance - User's available miles balance
 * @param options - Optional formatting options
 * @returns Formatted price string
 */
export function formatDisplayPrice(
  basePrice: number,
  paymentMethod: string,
  milesBalance: number = 0,
  options: { compact?: boolean } = {}
): string {
  const { displayPrice, displayUnit, milesUsed, remainingCash, isFullyMilesCovered } = 
    calculateDisplayPrice(basePrice, paymentMethod, milesBalance);
  
  const { compact = false } = options;
  
  if (paymentMethod === 'miles_installments' && milesUsed && milesUsed > 0) {
    if (isFullyMilesCovered) {
      const milesStr = compact ? formatMilesCompact(displayPrice) : displayPrice.toLocaleString();
      return `${milesStr} miles`;
    }
    const milesStr = compact ? formatMilesCompact(milesUsed) : milesUsed.toLocaleString();
    const cashAmount = remainingCash || 0;
    const cashStr = cashAmount.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    return `${milesStr} miles +\n$${cashStr}`;
  }
  
  if (displayUnit === '/week') {
    const weeklyStr = displayPrice.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `From $${weeklyStr}/week`;
  }
  
  if (displayUnit === ' miles') {
    const milesStr = compact ? formatMilesCompact(displayPrice) : displayPrice.toLocaleString();
    return `${milesStr} miles`;
  }
  
  // Only show decimals if the number has a fractional part; use en-US for thousands comma
  const isWholeNumber = displayPrice === Math.floor(displayPrice);
  const cashStr = isWholeNumber
    ? displayPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : displayPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${cashStr}`;
}

/**
 * Check if a booking option is fully refundable (Main Select, Flex, Anytime, Business Select, etc.)
 * @param option - Booking option with together/departing/returning
 * @returns true if the option is refundable
 */
export function isFullyRefundableOption(option: { together?: { option_title?: string; extensions?: unknown[] }; departing?: { option_title?: string; extensions?: unknown[] }; returning?: { option_title?: string; extensions?: unknown[] } }): boolean {
  const mainOption = option?.together || option?.departing || option?.returning;
  const title = (mainOption?.option_title || '').toLowerCase();
  const arr = mainOption?.extensions || [];
  const exts = arr.map((e: unknown) => {
    if (typeof e === 'string') return e.toLowerCase();
    if (e && typeof e === 'object' && (e as { text?: string; value?: string; label?: string }).text) return String((e as { text?: string }).text).toLowerCase();
    if (e && typeof e === 'object' && (e as { value?: string }).value) return String((e as { value?: string }).value).toLowerCase();
    if (e && typeof e === 'object' && (e as { label?: string }).label) return String((e as { label?: string }).label).toLowerCase();
    return '';
  }).filter(Boolean).join(' ');
  const combined = `${title} ${exts}`;
  if (combined.includes('non-refundable') || combined.includes('no refund') || combined.includes('no flex')) return false;
  const refundableTitles = ['main select', 'refundable', 'flex', 'fully refundable', 'economy flex', 'refundable fare', 'anytime', 'business select'];
  if (refundableTitles.some(r => title.includes(r))) return true;
  if (!exts) return false;
  return exts.includes('refundable') || exts.includes('free cancellation') || exts.includes('full refund');
}